import { LitElement, html, css, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { FieldComponent, type FormFieldSpec } from '../../core/bridge/connect-client';
import { groupFields, visibleFields } from '../components/integration/integration-form-model';
import { tOr, tEn, LocaleController } from '@vitesse/base-ui/i18n';
import '@vitesse/base-ui/elements/inputs/vitesse-text-input';
import '@vitesse/base-ui/elements/inputs/vitesse-number-input';
import '@vitesse/base-ui/elements/inputs/vitesse-toggle';
import '@vitesse/base-ui/elements/inputs/vitesse-radio-group';
import '@vitesse/base-ui/elements/inputs/vitesse-select';
import '@vitesse/base-ui/elements/inputs/vitesse-secret-input';

/**
 * <vitesse-schema-form> — renders a plugin's declarative FormSchema (the typed
 * component library). Each FieldComponent maps to the matching base-ui widget;
 * the frontend owns the widgets + styling, so every plugin's form looks native.
 * Per-field errors come from the backend (the authoritative validator).
 *
 * Two things the form does with the field list beyond rendering it:
 *   - it HIDES a field whose `showWhen` condition the current values do not meet
 *     (the OAuth boxes appear when `auth` is oauth2, and only then), and
 *   - it SECTIONS fields by `group` when more than one group is present, so a
 *     long connection form reads Connection / Auth / TLS instead of one list.
 * Both are no-ops for a form that declares neither — a plugin's form renders
 * exactly as it always has.
 *
 *   <vitesse-schema-form .fields=${fields} .values=${values} .errors=${errors}
 *       @form-change=${(e) => (values = e.detail.values)}></vitesse-schema-form>
 *
 * Emits `form-change` CustomEvent<{ values: Record<string,string> }> on every edit.
 */
@customElement('vitesse-schema-form')
export class VitesseSchemaForm extends LitElement {
  @property({ attribute: false }) fields: FormFieldSpec[] = [];
  @property({ attribute: false }) values: Record<string, string> = {};
  @property({ attribute: false }) errors: Record<string, string> = {};
  /** Optional section title rendered above the fields (the agent's .form-section h3). */
  @property() heading = '';

  constructor() {
    super();
    new LocaleController(this); // labels re-render on a language switch
  }

  /*
   * LABELS ARRIVE IN ENGLISH from the service that describes the form, and the
   * service is not where translation belongs — it would have to know the
   * viewer's language per request, for one string. So the browser translates by
   * FIELD KEY (integrationField.<key> in the locale files), with one guard: the
   * translation is used only when the server's label is the English that the
   * translation stands for. A connector that labels a shared key its own way
   * ("Host (nsqd)", "PG-wire port") keeps its wording rather than being silently
   * relabelled to the generic one. A key the locale files do not list renders the
   * server's label — never the key.
   */
  private labelOf(f: FormFieldSpec): string {
    const key = `integrationField.${f.key}`;
    return tEn(key) === f.label ? tOr(key, f.label) : f.label;
  }
  private groupOf(name: string): string {
    return tOr(`integrationGroup.${name.toLowerCase()}`, name);
  }

  static styles = css`
    :host {
      display: block;
    }
    h3.section-title {
      font-size: 16px;
      font-weight: 600;
      margin: 4px 0 16px;
      padding-bottom: 8px;
      border-bottom: 1px solid var(--border-color);
      color: var(--text-primary);
    }
    h4.group-title {
      font-size: 12px;
      font-weight: 600;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      margin: 20px 0 12px;
      color: var(--text-secondary);
    }
    .group:first-of-type h4.group-title {
      margin-top: 0;
    }
    .field {
      margin-bottom: 16px;
    }
    .field:last-child {
      margin-bottom: 0;
    }
    label {
      display: block;
      margin-bottom: 8px;
      font-size: 14px;
      font-weight: 500;
      color: var(--text-primary);
    }
    .req {
      color: var(--red);
      margin-left: 2px;
    }
    .help {
      margin-top: 4px;
      font-size: 12px;
      color: var(--text-secondary);
      font-style: italic;
    }
    .err {
      margin-top: 4px;
      font-size: 12px;
      color: var(--red);
    }
  `;

  private val(f: FormFieldSpec): string {
    return this.values[f.key] ?? f.default ?? '';
  }

  private set(key: string, value: string): void {
    this.values = { ...this.values, [key]: value };
    this.dispatchEvent(new CustomEvent('form-change', { detail: { values: this.values }, bubbles: true, composed: true }));
  }

  private renderInput(f: FormFieldSpec) {
    const v = this.val(f);
    const onChange = (e: CustomEvent) => this.set(f.key, String((e.detail as { value: unknown }).value));
    const opts = f.options.map((o) => ({ value: o, label: o }));
    switch (f.component) {
      case FieldComponent.NUMBER:
        return html`<vitesse-number-input .value=${v} placeholder=${f.placeholder} @change=${onChange}></vitesse-number-input>`;
      case FieldComponent.SELECT:
        return html`<vitesse-select .options=${opts} .value=${v} placeholder=${f.placeholder} @change=${onChange}></vitesse-select>`;
      case FieldComponent.RADIO:
        return html`<vitesse-radio-group .options=${opts} .value=${v} @change=${onChange}></vitesse-radio-group>`;
      case FieldComponent.TOGGLE:
        return html`<vitesse-toggle
          ?checked=${v === 'true'}
          @change=${(e: CustomEvent) => this.set(f.key, (e.detail as { checked: boolean }).checked ? 'true' : 'false')}
        ></vitesse-toggle>`;
      case FieldComponent.SECRET:
        return html`<vitesse-secret-input .value=${v} placeholder=${f.placeholder} @change=${onChange}></vitesse-secret-input>`;
      case FieldComponent.PEM:
        // Certificate material: many lines. The single-line control strips the
        // newlines out of a paste, and the driver's repair of that is a backstop,
        // not the fix.
        return html`<vitesse-secret-input multiline .value=${v} placeholder=${f.placeholder} @change=${onChange}></vitesse-secret-input>`;
      default: // TEXT + UNSPECIFIED
        return html`<vitesse-text-input .value=${v} placeholder=${f.placeholder} @change=${onChange}></vitesse-text-input>`;
    }
  }

  private renderField(f: FormFieldSpec) {
    return html`<div class="field">
      <label>${this.labelOf(f)}${f.required ? html`<span class="req">*</span>` : nothing}</label>
      ${this.renderInput(f)}
      ${this.errors[f.key]
        ? html`<div class="err">${this.errors[f.key]}</div>`
        : f.help
          ? html`<div class="help">${f.help}</div>`
          : nothing}
    </div>`;
  }

  render() {
    const shown = visibleFields(this.fields, this.values);
    const groups = groupFields(shown);
    // One group (or none declared) renders as the flat list it always was; the
    // headings only appear when there is something to divide.
    const sectioned = groups.length > 1;
    return html`${this.heading ? html`<h3 class="section-title">${this.heading}</h3>` : nothing}${groups.map(
      (g) => html`<div class="group">
        ${sectioned && g.group ? html`<h4 class="group-title">${this.groupOf(g.group)}</h4>` : nothing}
        ${g.fields.map((f) => this.renderField(f))}
      </div>`,
    )}`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'vitesse-schema-form': VitesseSchemaForm;
  }
}
